import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store/store";
import {
  fetchStates,
  fetchLgasByState,
} from "../store/slice/location/locationThunks";
import { fetchServices } from "../store/slice/service/serviceThunks";
import { registerProvider } from "../store/slice/provider/providerThunks";

interface ProviderRegistrationProps {
  onComplete?: () => void;
}

const ProviderRegistration: React.FC<ProviderRegistrationProps> = ({
  onComplete,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const { states, lgas } = useSelector((s: RootState) => s.location);
  const { services } = useSelector((s: RootState) => s.service);
  const { loading, error } = useSelector((s: RootState) => s.provider);

  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    businessName: "",
    businessAddress: "",
    stateId: "",
    lgaId: "",
    serviceId: "",
    price: "",
    experience: "",
    bio: "",
    latitude: "",
    longitude: "",
  });
  const [locationStatus, setLocationStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [locationError, setLocationError] = useState("");

  useEffect(() => {
    dispatch(fetchStates());
    dispatch(fetchServices());
  }, [dispatch]);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus("error");
      setLocationError("Geolocation isn't supported by this browser");
      return;
    }

    setLocationStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((prev) => ({
          ...prev,
          latitude: String(position.coords.latitude),
          longitude: String(position.coords.longitude),
        }));
        setLocationStatus("success");
      },
      (err) => {
        setLocationStatus("error");
        setLocationError(err.message || "Couldn't get your location");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stateId = e.target.value;
    setForm((prev) => ({ ...prev, stateId, lgaId: "" }));
    if (stateId) dispatch(fetchLgasByState(Number(stateId)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !form.fullName ||
      !form.phone ||
      !form.stateId ||
      !form.lgaId ||
      !form.serviceId
    ) {
      alert("Please fill all required fields");
      return;
    }

    const payload = {
      ...form,
      stateId: Number(form.stateId),
      lgaId: Number(form.lgaId),
      serviceId: Number(form.serviceId),
      price: form.price ? Number(form.price) : null,
      latitude: form.latitude ? Number(form.latitude) : undefined,
      longitude: form.longitude ? Number(form.longitude) : undefined,
    };

    try {
      await dispatch(registerProvider(payload as any));
      onComplete?.();
    } catch {
      // handled in slice
    }
  };

  return (
    <div className="mt-10 p-6 bg-gray-50 rounded-lg border border-gray-200">
      <h2 className="text-xl font-semibold mb-4 text-center">
        Handyman Details
      </h2>

      {error && <p className="text-red-600 text-center mb-3">{error}</p>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          placeholder="Full Name *"
          className="w-full p-2 border rounded"
          value={form.fullName}
          onChange={(e) => setForm({ ...form, fullName: e.target.value })}
        />

        <input
          type="text"
          placeholder="Phone Number *"
          className="w-full p-2 border rounded"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />

        <input
          type="text"
          placeholder="Business Name"
          className="w-full p-2 border rounded"
          value={form.businessName}
          onChange={(e) => setForm({ ...form, businessName: e.target.value })}
        />

        <input
          type="text"
          placeholder="Business Address"
          className="w-full p-2 border rounded"
          value={form.businessAddress}
          onChange={(e) =>
            setForm({ ...form, businessAddress: e.target.value })
          }
        />

        <select
          className="w-full p-2 border rounded"
          value={form.stateId}
          onChange={handleStateChange}
        >
          <option value="">Select State *</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <select
          className="w-full p-2 border rounded"
          value={form.lgaId}
          disabled={!form.stateId}
          onChange={(e) => setForm({ ...form, lgaId: e.target.value })}
        >
          <option value="">Select LGA *</option>
          {lgas.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>

        <select
          className="w-full p-2 border rounded"
          value={form.serviceId}
          onChange={(e) => setForm({ ...form, serviceId: e.target.value })}
        >
          <option value="">Select Service *</option>
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <input
          type="number"
          placeholder="Price (optional)"
          className="w-full p-2 border rounded"
          value={form.price}
          onChange={(e) => setForm({ ...form, price: e.target.value })}
        />

        <input
          type="text"
          placeholder="Years of Experience"
          className="w-full p-2 border rounded"
          value={form.experience}
          onChange={(e) => setForm({ ...form, experience: e.target.value })}
        />

        <textarea
          placeholder="Short Bio"
          className="w-full p-2 border rounded"
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
        />

        <div>
          <button
            type="button"
            onClick={handleUseMyLocation}
            disabled={locationStatus === "loading"}
            className="w-full border border-green-600 text-green-700 py-2 rounded font-semibold hover:bg-green-50 disabled:opacity-50"
          >
            {locationStatus === "loading"
              ? "Getting your location..."
              : locationStatus === "success"
              ? "Location captured ✓"
              : "Use my current location (optional)"}
          </button>
          {locationStatus === "error" && (
            <p className="text-red-600 text-sm mt-1">{locationError}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-green-600 text-white py-2 rounded font-bold hover:bg-green-700 disabled:bg-gray-400"
        >
          {loading ? "Saving..." : "Save Handyman Profile"}
        </button>
      </form>
    </div>
  );
};

export default ProviderRegistration;
